<template lang="pug">
  .tabs
    .tab-buttons(role="tablist" :class="themeClasses")
      button.btn-reset.ui-color-6.max-at-tweak(
        v-for="(t, index) in tabs"
        :key="t.code"
        @click="setTab(t)"
        @keydown="onKeydown($event, index)"
        :id="`tab-${t.code}`"
        :aria-controls="`tab-content-${t.code}`"
        role="tab"
        :aria-selected="t.code === selectedTabCode"
        :class="tabButtonClass(t)"
        :tabindex="t.code === selectedTabCode ? '0' : '-1'"
        ref="tabButton"
      )
        span.ada-tooltip Open
        | {{ t.title }}
        span.ada-tooltip Tab

    .selected-tab(:style="{ minHeight: minTabHeight }" :class="themeClasses")
      transition(name="fade" mode="out-in")
        .tab(
          v-for="(t) in tabs"
          v-if="t.code === selectedTabCode"
          :key="t.code"
          :id="`tab-content-${t.code}`"
          role="tabpanel"
          :aria-labelledby="`tab-${t.code}`"
        )
          slot(:name="t.code")

</template>

<script>
  export default {
    name: "Tabs",

    props: {
      theme: {
        type: String,
        default: 'default',
        validator: value => ['default', 'secondary', 'tertiary'].includes(value)
      },
      minTabHeight: {
        type: String,
        default: '38em'
      },
      tabs: {
        type: Array,
        default: () => ([])
      }
    },

    data() {
      return {
        selectedTabCodePriv: null,
        focusTabIndex: null,
      };
    },

    computed: {
      selectedTabCode() {
        if (!this.selectedTabCodePriv && this.tabs.length > 0) {
          return this.tabs[0].code;
        }
        return this.selectedTabCodePriv;
      },

      themeClasses() {
        return {
          'secondary-theme': this.theme === 'secondary',
          'tertiary-theme': this.theme === 'tertiary',
        };
      }
    },

    mounted() {
      this.focusTabIndex = this.tabs.findIndex(tab => tab.code === this.selectedTabCode);
    },

    methods: {
      setTab(newTab) {
        if (this.selectedTabCodePriv !== newTab.code) {
          this.selectedTabCodePriv = newTab.code;
          this.$emit('tab-set', newTab);
        }
      },

      onKeydown(event, index) {
        if (event.key === 'ArrowRight') {
          this.focusTabIndex = (index + 1) % this.tabs.length;
        } else if (event.key === 'ArrowLeft') {
          this.focusTabIndex = (index - 1 + this.tabs.length) % this.tabs.length;
        } else if (event.key === 'Enter') {
          this.setTab(this.tabs[index]);
          return;
        } else {
          return;
        }

        event.preventDefault();
        this.$nextTick(() => {
          this.$refs.tabButton[this.focusTabIndex].focus();
        });
      },

      tabButtonClass(t) {
        // Use computed property for selected tab code
        const selectedTabCode = this.selectedTabCode;
        return {
          'selected': t.code === selectedTabCode,
          'upper xs-f-small semi-bold': this.theme === 'default',
          'f-primary secondary-theme xs-f-medium semi-bold': this.theme === 'secondary',
          'f-secondary tertiary-theme xs-f-xlarge md-f-grande xl-f-xgrande': this.theme === 'tertiary',
        };
      },
    }
  };
</script>

<style scoped lang="stylus">
.tabs
  .tab-buttons
    border-bottom 1px solid ui-color-4

    &.secondary-theme
    &.tertiary-theme
      display flex
      justify-content center
      border-bottom unset

    button
      position relative
      display inline-block
      vertical-align top
      padding 0.25em 1.5em
      transition color 0.3s ease

      &.secondary-theme
        letter-spacing -1px
        color cw-color-11
        border-bottom 1px solid ui-color-4

        &.selected
          &:after
            content ''
            position absolute
            left 0
            right 0
            bottom -1px
            height 3px
            background cw-color-11

      &.tertiary-theme
        color ui-color-7
        padding: 0.25em 1em
        letter-spacing -1px

        &.selected
          color brand-color-1

          &:after
            height 4px
            bottom 0px
            color brand-color-1

      &.selected
        color text-color-1
        transition color 0.3s ease

        &:after
          content ''
          position absolute
          left 0
          right 0
          bottom -1px
          height 2px
          background text-color-1

  .selected-tab
    position relative

  .fade-enter-active, .fade-leave-active
    transition opacity 0.3s
  .fade-enter, .fade-leave-to
    opacity 0

</style>
